import type { CSSProperties, FC } from 'react'
import type { Industry } from '@/constants/home'

import { useEffect, useState } from 'react'
import { Clock3, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { hand } from '@/lib/fonts'
import { useInView } from '@/hooks/use-in-view'
import { Phone } from '@/components/kit/phone'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { industries, sampleWeeks } from '@/constants/home'

const formatTone: Record<string, string> = {
    Reel: 'bg-ember text-ink',
    Short: 'bg-ember text-ink',
    Carousel: 'bg-ink text-cream',
    Story: 'bg-butter text-ink',
    Live: 'bg-[#ff3b30] text-white',
    Photo: 'bg-sand text-ink'
}

export const PostTimeMachine: FC = () => {
    const [industry, setIndustry] = useState<Industry>('Fashion')
    const [shown, setShown] = useState(0)
    const [run, setRun] = useState(0)
    const { ref, inView } = useInView<HTMLDivElement>({ rootMargin: '0px 0px -25% 0px' })
    const week = sampleWeeks[industry]

    useEffect(() => {
        if (!inView) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setShown(week.length)

            return
        }

        setShown(0)

        let count = 0
        const timer = window.setInterval(() => {
            count += 1
            setShown(count)

            if (count >= week.length) window.clearInterval(timer)
        }, 620)

        return () => window.clearInterval(timer)
    }, [inView, industry, run, week.length])

    const best = week.reduce((winner, post) => (post.time === '18:42' ? post : winner), week[2])

    return (
        <section
            id='post-time-machine'
            aria-labelledby='time-machine-title'
            className='relative overflow-hidden bg-ink py-20 text-cream md:py-28'
        >
            <div className='mx-auto grid max-w-[1400px] items-center gap-14 px-5 md:px-10 lg:grid-cols-12'>
                <div className='lg:col-span-6'>
                    <SectionLabel
                        index='06'
                        tone='cream'
                    >
                        Post Time Machine
                    </SectionLabel>
                    <SplitText
                        as='h2'
                        id='time-machine-title'
                        lines={['Watch a week', 'build itself']}
                        italic='itself'
                        className='mt-5 text-[clamp(2.75rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                    />
                    <Reveal
                        as='p'
                        delay={150}
                        className='mt-6 max-w-md text-lg leading-relaxed text-cream/70'
                    >
                        Pick an industry and we will plan a sample week right there on the phone: formats, captions and the times your people are actually awake.
                    </Reveal>

                    <Reveal
                        delay={250}
                        className='mt-8'
                    >
                        <p
                            id='industry-label'
                            className='font-mono text-[11px] uppercase tracking-[0.16em] text-cream/50'
                        >
                            Your industry
                        </p>
                        <div
                            role='radiogroup'
                            aria-labelledby='industry-label'
                            className='mt-3 flex flex-wrap gap-2'
                        >
                            {industries.map(item => (
                                <button
                                    key={item}
                                    type='button'
                                    role='radio'
                                    aria-checked={industry === item}
                                    onClick={() => setIndustry(item)}
                                    className={cn(
                                        'rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300',
                                        industry === item ? 'border-ember bg-ember text-ink' : 'border-cream/20 text-cream hover:border-cream/50'
                                    )}
                                >
                                    {item}
                                </button>
                            ))}
                        </div>

                        <button
                            type='button'
                            onClick={() => setRun(value => value + 1)}
                            className='mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-cream/70 transition-colors hover:text-cream'
                        >
                            <RotateCcw className='size-3.5' />
                            Replay the week
                        </button>
                    </Reveal>

                    <p className={cn(hand.className, 'mt-10 max-w-sm -rotate-2 text-2xl leading-tight text-butter')}>
                        Tip: {best.day} at {best.time} is quiet in your category. That is where we would put the big one.
                    </p>
                </div>

                <div
                    ref={ref}
                    className='flex justify-center lg:col-span-6'
                >
                    <Phone className='w-[300px] sm:w-[330px]'>
                        <div className='flex h-full flex-col bg-paper text-ink'>
                            <div className='px-5 pb-3 pt-10'>
                                <p className='font-mono text-[10px] uppercase tracking-[0.16em] text-stone'>Updraft planner</p>
                                <p className='mt-1 font-heading text-xl font-bold leading-none tracking-[-0.03em]'>
                                    {industry}, next week
                                </p>
                                <div className='mt-3 h-1 overflow-hidden rounded-full bg-ink/10'>
                                    <span
                                        className='block h-full rounded-full bg-ember transition-[width] duration-500 ease-out'
                                        style={{ width: `${(shown / week.length) * 100}%` }}
                                    />
                                </div>
                            </div>

                            <ol
                                aria-live='polite'
                                className='flex flex-1 flex-col gap-1.5 overflow-hidden px-3 pb-4'
                            >
                                {week.map((post, index) => (
                                    <li
                                        key={`${industry}-${post.day}-${run}`}
                                        className={cn(
                                            'flex items-start gap-2.5 rounded-xl border border-ink/[0.07] bg-cream/70 px-2.5 py-2',
                                            index < shown ? 'pop-up' : 'invisible'
                                        )}
                                        style={{ '--delay': '0ms' } as CSSProperties}
                                    >
                                        <span className='w-8 shrink-0 pt-0.5 font-mono text-[10px] font-medium uppercase text-stone'>{post.day}</span>
                                        <span className='min-w-0 flex-1'>
                                            <span className={cn('inline-block rounded-full px-1.5 py-px font-mono text-[9px] uppercase tracking-[0.08em]', formatTone[post.format])}>
                                                {post.format}
                                            </span>
                                            <span className='mt-0.5 block truncate text-[12px] font-medium leading-snug'>{post.caption}</span>
                                        </span>
                                        <span className='inline-flex shrink-0 items-center gap-1 pt-0.5 font-mono text-[10px] text-ink/70'>
                                            <Clock3 className='size-3' />
                                            {post.time}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </Phone>
                </div>
            </div>
        </section>
    )
}
